// Public streams list — '/streams'.
//
// The public endpoint exposes portal-safe metadata only, so this route is
// available without an authenticated session. Live streams sort first; the
// tile under each name is a quiet neutral surface, never "no signal" art
// under a LIVE badge.
import { useMemo } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'

import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { InlineError, InlineEmpty } from '@/components/ui'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { liveFor } from '@/hooks/use-stream-status'
import { apiClient } from '@/lib/api-client'
import type { PublicStreamSummary, PublicStreamsResponse } from '@/lib/types'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/streams/')({
  component: StreamsIndexPage,
})

function sortStreams(streams: PublicStreamSummary[]): PublicStreamSummary[] {
  return [...streams].sort((a, b) => {
    if (a.is_live !== b.is_live) return a.is_live ? -1 : 1
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  })
}

function StreamsIndexPage() {
  useDocumentTitle('Streams')
  const { data, isLoading, isError } = useQuery<PublicStreamsResponse>({
    queryKey: ['streams', 'public'],
    refetchInterval: 10_000,
    queryFn: () =>
      apiClient.get<PublicStreamsResponse>('/streams/public/', {
        skipAuthRedirect: true,
      }),
  })
  const streams = useMemo(() => sortStreams(data?.streams ?? []), [data])

  return (
    <main
      data-testid="streams-index"
      className="mx-auto max-w-[1536px] px-4 py-6 sm:px-6 sm:py-8"
    >
      <div className="mb-5">
        <h1 className="text-xl font-semibold sm:text-2xl">Streams</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">Live broadcast sessions</p>
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-busy={true} aria-live="polite">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card key={index} className="flex gap-3 p-3 sm:flex-col">
              <Skeleton className="aspect-video w-28 shrink-0 rounded-md sm:w-full" />
              <div className="flex-1 space-y-2 py-1">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </Card>
          ))}
        </div>
      ) : isError ? (
        <InlineError message="Couldn't load streams. Try refreshing." />
      ) : streams.length === 0 ? (
        <InlineEmpty message="No streams yet." />
      ) : (
        <ul className="grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3">
          {streams.map((stream) => (
            <li key={stream.id}>
              <StreamCard stream={stream} />
            </li>
          ))}
        </ul>
      )}
    </main>
  )
}

function StreamCard({ stream }: { stream: PublicStreamSummary }) {
  const onAir = liveFor(stream)
  return (
    <Card
      className={cn(
        'h-full transition-colors hover:border-cyber-cyan/60',
        stream.is_live && 'border-cyber-green/50',
      )}
    >
      <Link
        data-testid="streams-row"
        to="/streams/$streamId"
        params={{ streamId: String(stream.id) }}
        aria-label={`Open stream ${stream.name}`}
        className="group flex h-full gap-3 rounded-[inherit] p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:flex-col"
      >
        <div
          aria-hidden="true"
          className={cn(
            'relative flex aspect-video w-28 shrink-0 items-center justify-center overflow-hidden rounded-md border sm:w-full',
            stream.is_live
              ? 'border-border bg-background-elevated'
              : 'border-border/60 bg-background',
          )}
        >
          {stream.is_live ? (
            <Badge variant="live" className="absolute left-2 top-2 gap-1.5 text-xs">
              <span className="relative inline-flex size-1.5 items-center justify-center">
                <span className="animate-live-dot absolute inline-flex h-full w-full rounded-full bg-cyber-green" />
              </span>
              LIVE
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground sm:text-sm">Offline</span>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
          <p className="truncate font-medium text-foreground transition-colors group-hover:text-cyber-cyan">
            {stream.name}
          </p>
          <p
            className="text-[13px] text-muted-foreground"
            title={onAir?.since ?? undefined}
          >
            {stream.is_live ? (onAir?.label ?? 'Live now') : 'Offline'}
          </p>
        </div>
      </Link>
    </Card>
  )
}
